import { useEffect, useMemo, useRef, useState } from "react";
import { Bluetooth, ChevronDownIcon } from "@/components/icons";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
} from "@/components/kit";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { LuckPrinterBleTransport } from "@/modules/chat-tools/domain/luck-printer/ble-transport";
import type { LuckContentMode } from "@/modules/chat-tools/domain/luck-printer/content-modes";
import { printShippingLabelsBatch } from "@/modules/chat-tools/domain/luck-printer/print-batch-labels";
import type { LuckPrinterDensity } from "@/modules/chat-tools/domain/luck-printer/protocol";
import {
  clearSavedLuckPrinterDevice,
  getSavedLuckPrinterContentMode,
  getSavedLuckPrinterDevice,
  getSavedLuckPrinterDensity,
} from "@/modules/chat-tools/domain/luck-printer/storage";
import { useShopSettingsQuery } from "@/modules/chat-tools/presentation/api/queries";
import {
  loadBatchLabelItems,
  type BatchPrintOrderRef,
} from "@/modules/chat-tools/presentation/lib/load-batch-label-items";
import {
  PrinterContentModeField,
  PrinterDensityField,
  type PrinterConnectionState,
  PrinterDeviceCard,
  PrinterStatusBanner,
} from "@/modules/chat-tools/presentation/ui/luck-printer-dialog-parts";
import { isWebBluetoothSupported } from "@/modules/chat-tools/presentation/lib/web-bluetooth";

type BatchBluetoothPrintDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orders: BatchPrintOrderRef[];
  title?: string;
  onComplete?: (result: { printed: number; failed: number }) => void;
  /** ພັບເປັນ floating chip ເພື່ອເບິ່ງໜ້າຢູ່ຂ້າງຫຼັງ (ໃຊ້ໃນໂໝດແພັກ) */
  minimizable?: boolean;
};

export function BatchBluetoothPrintDialog({
  open,
  onOpenChange,
  orders,
  title = "ພິມປ້າຍຊຸດ",
  onComplete,
  minimizable = false,
}: BatchBluetoothPrintDialogProps) {
  const transport = useMemo(() => new LuckPrinterBleTransport(), []);
  const settingsQuery = useShopSettingsQuery();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [savedDevice, setSavedDevice] = useState(getSavedLuckPrinterDevice);
  const [connectedName, setConnectedName] = useState<string | null>(null);
  const [density, setDensity] = useState<LuckPrinterDensity>(
    getSavedLuckPrinterDensity(),
  );
  const [contentMode, setContentMode] = useState<LuckContentMode>(
    getSavedLuckPrinterContentMode(),
  );
  const [status, setStatus] = useState<string | null>(null);
  const [statusVariant, setStatusVariant] = useState<"default" | "destructive">(
    "default",
  );
  const [isConnecting, setIsConnecting] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const prevOrdersRef = useRef(orders);

  const selectedOrders = useMemo(
    () => orders.filter((order) => selectedIds.includes(order.id)),
    [orders, selectedIds],
  );

  const deviceLabel =
    connectedName ?? savedDevice?.name ?? "ຍັງບໍ່ເລືອກເຄື່ອງພິມ";

  const connectionState: PrinterConnectionState = connectedName
    ? "connected"
    : savedDevice
      ? "saved"
      : "none";

  const isBusy = isConnecting || isPrinting;
  const allSelected =
    orders.length > 0 && selectedIds.length === orders.length;

  useEffect(() => {
    if (!open) {
      setMinimized(false);
      return;
    }
    if (prevOrdersRef.current !== orders) {
      setMinimized(false);
      prevOrdersRef.current = orders;
    }
    setSelectedIds(orders.map((order) => order.id));
    setSavedDevice(getSavedLuckPrinterDevice());
    setDensity(getSavedLuckPrinterDensity());
    setContentMode(getSavedLuckPrinterContentMode());
    setConnectedName(transport.connectedDevice?.name ?? null);
    setStatus(null);
    setStatusVariant("default");
  }, [open, orders, transport]);

  function toggleOrder(id: string, checked: boolean) {
    setSelectedIds((current) =>
      checked ? [...new Set([...current, id])] : current.filter((value) => value !== id),
    );
  }

  function toggleAll(checked: boolean) {
    setSelectedIds(checked ? orders.map((order) => order.id) : []);
  }

  async function handlePickDevice() {
    setIsConnecting(true);
    setStatus("ກຳລັງເລືອກເຄື່ອງພິມ...");
    setStatusVariant("default");
    try {
      const device = await transport.requestDevice();
      await transport.connect({ device });
      setConnectedName(device.name ?? "Printer");
      setSavedDevice(getSavedLuckPrinterDevice());
      setStatus("ເຊື່ອມຕໍ່ແລ້ວ — ພ້ອມພິມ");
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      if (err.name !== "NotFoundError") {
        setStatus(err.message);
        setStatusVariant("destructive");
      } else {
        setStatus(null);
      }
    } finally {
      setIsConnecting(false);
    }
  }

  async function handlePrint() {
    const settings = settingsQuery.data;
    if (!settings || selectedOrders.length === 0) return;

    setIsPrinting(true);
    setStatus(null);
    setStatusVariant("default");

    try {
      if (!transport.connectedDevice) {
        setStatus("ກຳລັງເຊື່ອມຕໍ່...");
        await transport.connect();
        setConnectedName(transport.connectedDevice?.name ?? "Printer");
        setSavedDevice(getSavedLuckPrinterDevice());
      }

      const { items, loadFailures } = await loadBatchLabelItems(
        selectedOrders,
        settings,
        (message) => {
          setStatus(message);
          setStatusVariant("default");
        },
      );

      if (loadFailures.length > 0) {
        toast.error(`ໂຫຼດອໍເດີ້ບໍ່ສຳເລັດ ${loadFailures.length} ລາຍການ`);
      }

      if (items.length === 0) {
        throw new Error("ບໍ່ມີປ້າຍທີ່ພິມໄດ້");
      }

      const result = await printShippingLabelsBatch({
        items,
        density,
        contentMode,
        transport,
        onProgress: (progress) => {
          setStatus(progress.message);
          setStatusVariant("default");
        },
      });

      onComplete?.({
        printed: result.printed,
        failed: result.failures.length + loadFailures.length,
      });

      if (result.failures.length === 0 && loadFailures.length === 0) {
        toast.success(`ພິມປ້າຍ ${result.printed} ແຜ່ນແລ້ວ`);
        onOpenChange(false);
        return;
      }

      toast.error(
        `ພິມສຳເລັດ ${result.printed} · ລົ້ມເຫຼວ ${result.failures.length + loadFailures.length}`,
      );
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      toast.error("ພິມຊຸດບໍ່ສຳເລັດ", { description: err.message });
      setStatus(err.message);
      setStatusVariant("destructive");
    } finally {
      setIsPrinting(false);
    }
  }

  function handleForgetDevice() {
    clearSavedLuckPrinterDevice();
    setSavedDevice(null);
    setConnectedName(null);
    void transport.disconnect();
    setStatus("ລືມເຄື່ອງພິມແລ້ວ");
    setStatusVariant("default");
  }

  function handleMinimize() {
    if (!minimizable) return;
    setMinimized(true);
  }

  function handleExpand() {
    setMinimized(false);
  }

  const dialogBody = (
    <>
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto py-1">
        <PrinterDeviceCard
          deviceLabel={deviceLabel}
          connectionState={connectionState}
          isConnecting={isConnecting}
          canForget={Boolean(savedDevice)}
          onConnect={() => void handlePickDevice()}
          onForget={handleForgetDevice}
          disabled={isBusy}
        />

        <div className="flex flex-col gap-4 rounded-xl border border-secondary/20 bg-muted/10 p-3">
          <PrinterContentModeField
            value={contentMode}
            onChange={setContentMode}
            disabled={isBusy}
          />
          <PrinterDensityField
            value={density}
            onChange={setDensity}
            disabled={isBusy}
          />
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm">ອໍເດີ້</span>
              <Badge variant="secondary" className="rounded-full">
                {selectedOrders.length} ລາຍການ
              </Badge>
            </div>
            <label
              htmlFor="batch-print-select-all"
              className="flex cursor-pointer items-center gap-2 text-muted-foreground text-xs"
            >
              <Checkbox
                id="batch-print-select-all"
                checked={allSelected}
                disabled={isBusy}
                onCheckedChange={(value) => toggleAll(value === true)}
              />
              ເລືອກທັງໝົດ
            </label>
          </div>
          <div className="max-h-48 overflow-y-auto rounded-xl border border-secondary/20 p-2">
            <div className="flex flex-col gap-1">
              {orders.map((order) => {
                const checkboxId = `batch-print-${order.id}`;
                return (
                  <label
                    key={order.id}
                    htmlFor={checkboxId}
                    className="flex cursor-pointer items-start gap-2 rounded-lg px-2 py-1.5 hover:bg-muted/40"
                  >
                    <Checkbox
                      id={checkboxId}
                      checked={selectedIds.includes(order.id)}
                      disabled={isBusy}
                      onCheckedChange={(value) =>
                        toggleOrder(order.id, value === true)
                      }
                    />
                    <span className="min-w-0 flex-1 text-sm leading-snug">
                      <span className="font-medium">{order.customerName}</span>
                      <span className="mt-0.5 block font-mono text-muted-foreground text-xs">
                        {order.orderNumber}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        <PrinterStatusBanner
          status={status}
          isBusy={isBusy && Boolean(status)}
          variant={statusVariant}
        />
      </div>

      <DialogFooter className="border-secondary/20 border-t pt-4 sm:justify-stretch">
        <Button
          type="button"
          size="lg"
          className="w-full rounded-full"
          disabled={
            selectedOrders.length === 0 ||
            isConnecting ||
            settingsQuery.isLoading
          }
          isLoading={isPrinting}
          onClick={() => void handlePrint()}
        >
          ພິມ {selectedOrders.length} ປ້າຍ
        </Button>
      </DialogFooter>
    </>
  );

  const floatingChip =
    open && minimized && minimizable ? (
      <button
        type="button"
        onClick={handleExpand}
        className="fixed right-4 bottom-[calc(var(--mobile-bottom-nav,0px)+9.5rem)] z-30 flex max-w-[min(18rem,calc(100vw-2rem))] items-center gap-2 rounded-full border border-secondary/30 bg-background/95 px-4 py-3 shadow-lg backdrop-blur supports-backdrop-filter:bg-background/90"
        aria-label="ເປີດໜ້າພິມປ້າຍ"
      >
        <span
          className={cn(
            "flex size-10 shrink-0 items-center justify-center rounded-full bg-secondary/15 text-secondary",
            isBusy && "animate-pulse",
          )}
        >
          <Bluetooth className="size-5" />
        </span>
        <span className="min-w-0 flex-1 text-left">
          <span className="block font-medium text-sm leading-tight">
            {isPrinting ? "ກຳລັງພິມ..." : isConnecting ? "ກຳລັງເຊື່ອມ..." : "ພິມປ້າຍ"}
          </span>
          <span className="block truncate text-muted-foreground text-xs">
            {status ??
              `${selectedOrders.length} ອໍເດີ້ · ແຕະເພື່ອເປີດ`}
          </span>
        </span>
        <Badge variant="secondary" className="shrink-0 rounded-full tabular-nums">
          {selectedOrders.length}
        </Badge>
      </button>
    ) : null;

  if (!isWebBluetoothSupported()) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{title}</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground text-sm">
            Browser ນີ້ບໍ່ຮອງຮັບ Web Bluetooth — ໃຊ້ Chrome desktop ຫຼື
            ເປີດປ້າຍທີລະອໍເດີ້ແທນ
          </p>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      {floatingChip}
      <Dialog open={open && !minimized} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[90dvh] flex-col gap-0 overflow-hidden sm:max-w-lg">
          <DialogHeader className="gap-2 sm:text-left">
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <DialogTitle className="font-display text-xl">{title}</DialogTitle>
                <DialogDescription>
                  ເລືອກ {selectedOrders.length} / {orders.length} ອໍເດີ້
                </DialogDescription>
              </div>
              {minimizable ? (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="shrink-0 rounded-full"
                  title="ພັບເພື່ອເບິ່ງລາຍການ"
                  aria-label="ພັບເພື່ອເບິ່ງລາຍການ"
                  onClick={handleMinimize}
                >
                  <ChevronDownIcon />
                </Button>
              ) : null}
            </div>
          </DialogHeader>

          {dialogBody}
        </DialogContent>
      </Dialog>
    </>
  );
}
